import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import AnalisiMap from '../components/analisi/AnalisiMap';

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('it-IT'));
const TEC_COL = { FTTH: 'var(--info)', FTTC: 'var(--info)', FWA: 'var(--info)', ADSL: 'var(--faint)' };

const cnt = (v) => nf(Math.round(v));
const pct = (v) => `${v.toFixed(0)}%`;
const METRICS = [
  { key: 'lead_caldi', short: 'Lead caldi', base: [16, 160, 80], fmt: cnt, value: (d) => d.lead_caldi },
  { key: 'ftth', short: 'FTTH', base: [37, 99, 235], fmt: cnt, value: (d) => d.ftth },
  { key: 'fwa', short: 'FWA', base: [168, 85, 247], fmt: cnt, value: (d) => d.fwa },
  { key: 'fttc', short: 'FTTC', base: [14, 165, 233], fmt: cnt, value: (d) => d.fttc },
  { key: 'futuri', short: 'Futuri FTTH', base: [245, 158, 11], fmt: cnt, value: (d) => d.futuri },
  { key: 'pftth', short: '% FTTH', base: [29, 78, 216], fmt: pct, value: (d) => (d.coperti ? (d.ftth / d.coperti) * 100 : 0) },
  { key: 'scoperti', short: 'Scoperti', base: [234, 88, 12], fmt: cnt, value: (d) => d.scoperti },
  { key: 'nontim', short: 'NON TIM', base: [22, 163, 74], fmt: cnt, value: (d) => d.nontim },
  { key: 'vodafone', short: 'Vodafone', base: [230, 0, 0], fmt: cnt, value: (d) => d.vodafone },
];

function Card({ title, right, children, style }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 16, ...style }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 14 }}>{title}</h2>
          <div style={{ marginLeft: 'auto' }}>{right}</div>
        </div>
      )}
      {children}
    </div>
  );
}

function MappaTab() {
  const [data, setData] = useState(null);
  const [metricKey, setMetricKey] = useState('lead_caldi');
  const [drillRegion, setDrillRegion] = useState(null);
  const [selProv, setSelProv] = useState(null);
  const [comuni, setComuni] = useState(null);

  useEffect(() => { axios.get(`${API}/analisi/geo`).then((r) => setData(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!selProv) { setComuni(null); return; }
    axios.get(`${API}/analisi/comuni`, { params: { provincia: selProv, metric: (metricKey === 'pftth' ? 'ftth' : metricKey), limit: 10 } })
      .then((r) => setComuni(r.data?.comuni || [])).catch(() => setComuni([]));
  }, [selProv, metricKey]);

  const metric = METRICS.find((m) => m.key === metricKey) || METRICS[0];
  const tot = data ? Object.values(data.province).reduce((a, d) => {
    ['coperti', 'ftth', 'fwa', 'fttc', 'futuri', 'scoperti', 'da_verificare', 'lead_caldi'].forEach((k) => { a[k] += d[k] || 0; });
    return a;
  }, { coperti: 0, ftth: 0, fwa: 0, fttc: 0, futuri: 0, scoperti: 0, da_verificare: 0, lead_caldi: 0 }) : null;

  const selData = selProv && data ? data.province[selProv] : (drillRegion && data ? data.regioni[drillRegion] : null);
  const maxC = comuni && comuni.length ? Math.max(1, ...comuni.map((c) => metric.value(c))) : 1;

  return (
    <div>
      {/* KPI strip */}
      {tot && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          {[['Coperti', tot.coperti, 'var(--info)'], ['FTTH', tot.ftth, 'var(--info)'], ['FWA', tot.fwa, 'var(--info)'], ['Futuri FTTH', tot.futuri, 'var(--warn)'], ['Scoperti', tot.scoperti, 'var(--warn)'], ['Lead caldi', tot.lead_caldi, '#10a050']].map(([l, v, c]) => (
            <div key={l} style={{ flex: 1, minWidth: 120, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: '10px 14px' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{nf(v)}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* metric chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {METRICS.map((m) => (
          <button key={m.key} onClick={() => setMetricKey(m.key)}
            style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${metricKey === m.key ? `rgb(${m.base})` : 'var(--line)'}`,
              background: metricKey === m.key ? `rgb(${m.base})` : '#fff',
              color: metricKey === m.key ? '#fff' : 'var(--muted)',
            }}>{m.short}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Card style={{ flex: '1 1 560px', minWidth: 320, padding: 10 }}
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <button onClick={() => { setDrillRegion(null); setSelProv(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: drillRegion ? 'var(--info)' : 'var(--text)', fontWeight: 700, fontSize: 13, padding: 0 }}>Italia</button>
              {drillRegion && <span style={{ color: 'var(--faint)' }}>›</span>}
              {drillRegion && <b>{drillRegion}</b>}
            </span>
          }
          right={<span style={{ fontSize: 11, color: 'var(--faint)' }}>{drillRegion ? 'clicca una provincia' : 'clicca una regione per entrare'}</span>}
        >
          <div style={{ height: 460 }}>
            <AnalisiMap data={data} metric={metric} drillRegion={drillRegion} selectedProv={selProv}
              onRegionClick={(k) => { setDrillRegion(k); setSelProv(null); }}
              onProvinceClick={(k) => setSelProv(k)} />
          </div>
        </Card>

        {/* pannello dettaglio (cross-filter) */}
        <Card style={{ flex: '1 1 280px', minWidth: 260 }} title={selProv || drillRegion || 'Dettaglio'}>
          {!selData ? (
            <div style={{ color: 'var(--faint)', fontSize: 13 }}>Seleziona una regione o provincia sulla mappa.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {[['FTTH', selData.ftth, 'var(--info)'], ['FWA', selData.fwa, 'var(--info)'], ['FTTC', selData.fttc, 'var(--info)'], ['Futuri FTTH', selData.futuri, 'var(--warn)'], ['Scoperti', selData.scoperti, 'var(--warn)'], ['Lead caldi', selData.lead_caldi, '#10a050']].map(([l, v, c]) => (
                  <div key={l} style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '7px 10px' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, color: c }}>{nf(v)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--muted)' }}>{l}</div>
                  </div>
                ))}
              </div>
              {selProv && comuni && comuni.length > 0 && (
                <>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', margin: '4px 0 6px' }}>Top comuni · {metric.short}</div>
                  {comuni.map((c) => {
                    const v = metric.value(c); const pct = Math.round((v / maxC) * 100);
                    return (
                      <div key={c.comune} style={{ marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5 }}>
                          <span style={{ textTransform: 'capitalize' }}>{(c.comune || '').toLowerCase()}</span>
                          <b>{metric.fmt(v)}</b>
                        </div>
                        <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: `rgb(${metric.base})` }} />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function FuturiTab() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/analisi/futuri-ftth`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--faint)' }}>Caricamento…</div>;
  const TC = { FTTC: 'var(--info)', FWA: 'var(--info)', ADSL: 'var(--faint)' };
  const totFuturi = d.per_tec.reduce((a, t) => a + t.n, 0);
  const totNonTim = d.per_tec.reduce((a, t) => a + t.nontim, 0);
  const maxProv = Math.max(1, ...d.top_province.map((p) => p.futuri));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card title="Candidati al passaggio a FTTH (FTTC · FWA · ADSL)"
        right={<span style={{ fontSize: 12, color: 'var(--muted)' }}><b>{nf(totFuturi)}</b> · {nf(totNonTim)} NON TIM</span>}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {d.per_tec.map((t) => (
            <div key={t.tec} style={{ flex: '1 1 160px', minWidth: 150, border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: TC[t.tec] || 'var(--faint)' }} />
                <b style={{ fontSize: 14 }}>{t.tec}</b>
              </div>
              <div style={{ fontSize: 24, fontWeight: 800, marginTop: 6, color: 'var(--warn)' }}>{nf(t.n)}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>di cui <b>{nf(t.nontim)}</b> NON TIM</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--faint)', margin: '10px 0 0' }}>
          L'agente copertura li ricontrolla con timing breve: quando passano a fibra compaiono in “Diventati FTTH” (badge NEW + notifica, se attiva).
        </p>
      </Card>

      <Card title="Dove sono — top province">
        {d.top_province.length === 0 ? <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessun dato.</div> : d.top_province.map((p) => {
          const w = Math.round((p.futuri / maxProv) * 100);
          return (
            <div key={p.prov} style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                <span style={{ textTransform: 'capitalize' }}>{(p.prov || '').toLowerCase()}</span>
                <b>{nf(p.futuri)} <span style={{ color: 'var(--faint)', fontWeight: 600 }}>· {nf(p.nontim)} NON TIM</span></b>
              </div>
              <div style={{ height: 7, background: 'var(--surface-2)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${w}%`, height: '100%', background: 'var(--warn)' }} />
              </div>
            </div>
          );
        })}
      </Card>

      <Card title={`Diventati FTTH${d.diventati_tot ? ` · ${nf(d.diventati_tot)}` : ''}`}>
        {d.diventati_tot === 0 ? (
          <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessuno ancora rilevato. Appariranno qui quando un FTTC/FWA/ADSL passa a fibra — sono i lead da contattare per primi.</div>
        ) : (
          d.diventati.map((x, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '5px 0', borderBottom: '1px solid var(--surface-2)' }}>
              <span style={{ textTransform: 'capitalize' }}>{(x.nome || x.numero || '').toString().toLowerCase()} <span style={{ color: 'var(--faint)' }}>· {(x.comune || '').toLowerCase()} ({x.prov})</span></span>
              <span style={{ color: 'var(--muted)' }}>{x.at ? new Date(x.at).toLocaleDateString('it-IT') : ''}</span>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

function VelocitaTab() {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/analisi/velocita`).then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--faint)' }}>Caricamento…</div>;

  const TECS = ['FTTH', 'FTTC', 'FWA', 'ADSL'];
  const maxDown = Math.max(1, ...d.per_tecnologia.map((t) => t.down_max || 0));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card title="Velocità per tecnologia">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {d.per_tecnologia.map((t) => (
            <div key={t.tec} style={{ flex: '1 1 180px', minWidth: 170, border: '1px solid var(--line)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: TEC_COL[t.tec] || 'var(--faint)' }} />
                <b style={{ fontSize: 14 }}>{t.tec}</b>
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>{nf(t.n)} linee</span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>{nf(t.down_avg)}<span style={{ fontSize: 12, color: 'var(--faint)', fontWeight: 600 }}> Mbps down</span></div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>up {nf(t.up_avg)} · lat {t.lat_avg} ms</div>
              <div style={{ height: 6, background: 'var(--line)', borderRadius: 4, overflow: 'hidden', marginTop: 8 }}>
                <div style={{ width: `${Math.round((t.down_avg / maxDown) * 100)}%`, height: '100%', background: TEC_COL[t.tec] || 'var(--faint)' }} />
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--faint)', marginTop: 4 }}>range {nf(t.down_min)}–{nf(t.down_max)} Mbps</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Download vs Upload (per linea coperta)" right={<span style={{ fontSize: 11, color: 'var(--faint)' }}>{nf(d.punti.length)} punti</span>}>
        <div style={{ height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis type="number" dataKey="down" name="Download" unit=" Mb" tick={{ fontSize: 11 }} label={{ value: 'Download (Mbps)', position: 'bottom', fontSize: 11 }} />
              <YAxis type="number" dataKey="up" name="Upload" unit=" Mb" tick={{ fontSize: 11 }} label={{ value: 'Upload (Mbps)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
              <ZAxis range={[24, 24]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={(v) => nf(v)}
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '6px 9px', fontSize: 12 }}>
                      <b>{p.tec}</b> · {p.prov}<br />↓ {nf(p.down)} · ↑ {nf(p.up)} Mbps<br />
                      <span style={{ color: 'var(--muted)' }}>{(p.nome || '').slice(0, 32)}</span>
                    </div>
                  );
                }} />
              <Legend />
              {TECS.map((t) => (
                <Scatter key={t} name={t} data={d.punti.filter((p) => p.tec === t)} fill={TEC_COL[t]} fillOpacity={0.6} />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

export default function AnalisiPage() {
  const [tab, setTab] = useState('mappa');
  const tabBtn = (id, label) => (
    <button onClick={() => setTab(id)} style={{
      padding: '8px 16px', border: 'none', borderBottom: `2px solid ${tab === id ? 'var(--info)' : 'transparent'}`,
      background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === id ? 'var(--info)' : 'var(--muted)',
    }}>{label}</button>
  );
  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>Analisi</h1>
      <p style={{ margin: '0 0 14px', color: 'var(--muted)', fontSize: 13 }}>Copertura per tecnologia: mappa interattiva, candidati al passaggio a fibra e qualità della rete.</p>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--line)', marginBottom: 16 }}>
        {tabBtn('mappa', '🗺 Mappa')}
        {tabBtn('futuri', '🚀 Futuri FTTH')}
        {tabBtn('velocita', '⚡ Qualità rete')}
      </div>
      {tab === 'mappa' ? <MappaTab /> : tab === 'futuri' ? <FuturiTab /> : <VelocitaTab />}
    </div>
  );
}
